/** 财务收款 Mock数据 */

import type {
  FinanceReceivable,
  PaymentRecord,
  PaymentPlan,
  WaterSlip,
  ExchangeRate,
  ExchangeRecord,
  PaymentStructureItem,
  FinanceStats,
} from '../types';
import {
  PaymentStatus,
  PaymentMethod,
  WaterSlipStatus,
  ReceivableDisplayStatus,
  PaymentNode,
} from '../types';
import { randomFloat, randomPick, randomTimestamp } from '../utils/mockHelper';
import { mockOrders } from './orders';

const buyers: string[] = [
  'FreshFoods Inc.',
  'EuroGrocers Ltd.',
  'TokyoFresh Co.',
  'GlobalSpice LLC',
  'MiddleEast Foods',
  'Pacific Rim Imports',
  'Nordic Food Group',
  'African Trade Hub',
  'LatinFresh S.A.',
  'Southeast Asia Foods',
  'Australian Wholesale',
  'Canadian Grocers Co.',
];

const currencies = ['USD', 'USD', 'USD', 'USD', 'USD', 'USD', 'CNY', 'CNY', 'CNY', 'AED', 'AED'];

// ===== 逾期状态计算辅助函数 =====

/** 计算应收显示状态（根据到期日 vs 今天） */
function computeDisplayStatus(dueDate: Date, paidAmount: number, amount: number): {
  displayStatus: ReceivableDisplayStatus;
  overdueDays: number;
} {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);

  const diffTime = today.getTime() - due.getTime();
  const overdueDays = Math.max(0, Math.ceil(diffTime / 86400000));
  const daysUntilDue = Math.ceil((due.getTime() - today.getTime()) / 86400000);

  // 已全额收款 → 已完成
  if (paidAmount >= amount) {
    return { displayStatus: ReceivableDisplayStatus.COMPLETED, overdueDays: 0 };
  }
  // 逾期 ≥ 7天
  if (overdueDays >= 7) {
    return { displayStatus: ReceivableDisplayStatus.OVERDUE_7D, overdueDays };
  }
  // 逾期 1-6天
  if (overdueDays >= 1) {
    return { displayStatus: ReceivableDisplayStatus.OVERDUE_3D, overdueDays };
  }
  // 15日内到期
  if (daysUntilDue >= 0 && daysUntilDue <= 15) {
    return { displayStatus: ReceivableDisplayStatus.DUE_SOON, overdueDays: 0 };
  }
  // 正常
  return { displayStatus: ReceivableDisplayStatus.NORMAL, overdueDays: 0 };
}

/** 根据 displayStatus 推算 waterSlipStatus */
function deduceWaterSlipStatus(displayStatus: ReceivableDisplayStatus): WaterSlipStatus {
  if (displayStatus === ReceivableDisplayStatus.COMPLETED) {
    return WaterSlipStatus.VERIFIED;
  }
  // 未完成订单: 70% PENDING, 30% UNSUBMITTED
  return Math.random() < 0.7 ? WaterSlipStatus.PENDING : WaterSlipStatus.UNSUBMITTED;
}

// ===== 收款记录生成 =====

function generatePaymentRecords(index: number, amount: number, paidAmount: number): PaymentRecord[] {
  if (paidAmount <= 0) return [];
  const count = (index % 3) + 1;
  const sliceAmount = paidAmount / count;
  return Array.from({ length: count }, (_, i) => ({
    id: `PAY${String(index + 1).padStart(3, '0')}_${i + 1}`,
    amount: parseFloat(sliceAmount.toFixed(2)),
    method: randomPick([PaymentMethod.T_T, PaymentMethod.L_C, PaymentMethod.WIRE_TRANSFER, PaymentMethod.BLU_CREDIT]),
    transactionNo: `TXN${Date.now().toString(36).toUpperCase()}${i}`,
    paidAt: randomTimestamp(60),
    status: 'SUCCESS',
  }));
}

// ===== 1. 应收台账生成 (28条) =====

function generateReceivable(index: number): FinanceReceivable {
  const currency = currencies[index % currencies.length];
  const amount = parseFloat(randomFloat(10000, 150000, 2).toFixed(2));
  const isFullyPaid = index % 5 === 0;
  const paidAmount = isFullyPaid
    ? amount
    : parseFloat(randomFloat(0, amount * 0.9, 2).toFixed(2));

  // dueDate: 分布在过去30天到未来45天
  const today = new Date();
  const dueOffset = (index - 6) * 5; // 部分逾期，部分即将到期
  const dueDate = new Date(today.getTime() + dueOffset * 86400000);

  const { displayStatus, overdueDays } = computeDisplayStatus(dueDate, paidAmount, amount);
  const waterSlipStatus = deduceWaterSlipStatus(displayStatus);

  // paymentNode 分布
  const paymentNodes = [
    PaymentNode.T_T_DEPOSIT,  // 30%
    PaymentNode.T_T_DEPOSIT,
    PaymentNode.T_T_DEPOSIT,
    PaymentNode.T_T_BALANCE,  // 35%
    PaymentNode.T_T_BALANCE,
    PaymentNode.T_T_BALANCE,
    PaymentNode.T_T_BALANCE,
    PaymentNode.FULLY_PAID,   // 20%
    PaymentNode.FULLY_PAID,
    PaymentNode.L_C,          // 8%
    PaymentNode.BLU_CREDIT,   // 7%
  ];
  const paymentNode = paymentNodes[index % paymentNodes.length];

  const PAYMENT_NODE_LABELS: Record<PaymentNode, string> = {
    [PaymentNode.T_T_DEPOSIT]: 'T/T 30%定金',
    [PaymentNode.T_T_BALANCE]: 'T/T 70%尾款',
    [PaymentNode.FULLY_PAID]: '全额已付',
    [PaymentNode.L_C]: 'L/C',
    [PaymentNode.BLU_CREDIT]: 'BluCredit',
  };

  const yearMonth = 26;
  const orderNo = `BLU-${yearMonth}${String(7).padStart(2, '0')}-${String(index + 1).padStart(4, '0')}`;
  const bluCreditApplied = paymentNode === PaymentNode.BLU_CREDIT;

  return {
    id: `FIN${String(index + 1).padStart(3, '0')}`,
    orderId: `ORD${String(index + 1).padStart(3, '0')}`,
    orderNo,
    buyerName: buyers[index % buyers.length],
    amount,
    paidAmount,
    currency,
    dueDate: dueDate.toISOString(),
    status: PaymentStatus.PARTIALLY_PAID,
    paymentRecords: generatePaymentRecords(index, amount, paidAmount),
    bluCreditApplied,
    bluCreditAmount: bluCreditApplied ? parseFloat(randomFloat(5000, 20000, 2).toFixed(2)) : 0,
    waterSlipStatus,
    displayStatus,
    paymentNode,
    paymentNodeLabel: PAYMENT_NODE_LABELS[paymentNode],
    overdueDays,
  };
}

// ===== 2. 收款计划生成 (15条) =====

function generatePaymentPlan(index: number, receivables: FinanceReceivable[]): PaymentPlan {
  const source = receivables[index % receivables.length];
  const today = new Date();
  const plannedDate = new Date(today.getTime() + randomFloat(1, 30, 0) * 86400000);

  const statuses = ['pending', 'pending', 'pending', 'pending', 'completed', 'completed', 'overdue'];
  const status = statuses[index % statuses.length];

  return {
    id: `PLAN${String(index + 1).padStart(3, '0')}`,
    orderId: source.orderId,
    orderNo: source.orderNo,
    buyerName: source.buyerName,
    plannedAmount: parseFloat(randomFloat(5000, source.amount * 0.7, 2).toFixed(2)),
    currency: source.currency,
    plannedDate: plannedDate.toISOString(),
    paymentNode: source.paymentNode,
    status,
  };
}

// ===== 3. 水单核销生成 (3条) =====

function generateWaterSlip(index: number, receivables: FinanceReceivable[]): WaterSlip {
  const source = receivables[(index * 5 + 3) % receivables.length];
  const statuses = [WaterSlipStatus.VERIFIED, WaterSlipStatus.PENDING, WaterSlipStatus.PENDING];
  const status = statuses[index];

  return {
    id: `WS${String(index + 1).padStart(3, '0')}`,
    orderId: source.orderId,
    orderNo: source.orderNo,
    slipNo: `WS-2025-07${String(index + 1).padStart(3, '0')}`,
    amount: source.paidAmount > 0 ? source.paidAmount : parseFloat(randomFloat(10000, 50000, 2).toFixed(2)),
    currency: source.currency,
    submitDate: new Date(Date.now() - randomFloat(1, 30, 0) * 86400000).toISOString(),
    status,
    verifiedAt: status === WaterSlipStatus.VERIFIED ? new Date().toISOString() : undefined,
  };
}

// ===== 4. 汇兑记录生成 (12条) =====

function generateExchangeRecord(index: number): ExchangeRecord {
  const pairs = [
    { from: 'USD', to: 'CNY' },
    { from: 'USD', to: 'AED' },
    { from: 'CNY', to: 'USD' },
  ];
  const pair = pairs[index % pairs.length];
  const rate =
    pair.from === 'USD' && pair.to === 'CNY' ? 7.2368 + randomFloat(-0.05, 0.05, 4) :
    pair.from === 'USD' && pair.to === 'AED' ? 3.6725 + randomFloat(-0.01, 0.01, 4) :
    /* CNY → USD */ 0.1382 + randomFloat(-0.002, 0.002, 4);

  const fromAmount = parseFloat(randomFloat(5000, 100000, 2).toFixed(2));
  const orderNo = `BLU-2607-${String(index + 1).padStart(4, '0')}`;
  const buyer = buyers[index % buyers.length];

  return {
    id: `EXR${String(index + 1).padStart(3, '0')}`,
    orderNo,
    buyer,
    date: new Date(Date.now() - randomFloat(1, 30, 0) * 86400000).toISOString(),
    fromCurrency: pair.from,
    toCurrency: pair.to,
    rate: parseFloat(rate.toFixed(4)),
    fromAmount,
    toAmount: parseFloat((fromAmount * rate).toFixed(2)),
    remark: pair.from === 'USD' && pair.to === 'CNY' ? '尾款结汇' :
            pair.from === 'USD' && pair.to === 'AED' ? '定金换汇' : '采购付款',
  };
}

// ===== 5. 汇兑记录面板数据 (3条当前汇率) =====

function generateExchangeRates(): ExchangeRate[] {
  return [
    {
      id: 'RATE-USD-CNY',
      fromCurrency: 'USD',
      toCurrency: 'CNY',
      rate: 7.2368,
      changePercent: 0.12,
      updatedAt: new Date().toISOString(),
    },
    {
      id: 'RATE-USD-AED',
      fromCurrency: 'USD',
      toCurrency: 'AED',
      rate: 3.6725,
      changePercent: 0.0,
      updatedAt: new Date().toISOString(),
    },
    {
      id: 'RATE-CNY-USD',
      fromCurrency: 'CNY',
      toCurrency: 'USD',
      rate: 0.1382,
      changePercent: -0.08,
      updatedAt: new Date().toISOString(),
    },
  ];
}

// ===== 6. 收款结构数据 (4段) =====

function generatePaymentStructure(): PaymentStructureItem[] {
  const totalReceivable = 8200000;
  return [
    {
      method: PaymentNode.T_T_DEPOSIT,
      label: 'T/T 定金',
      percentage: 40,
      amount: Math.round(totalReceivable * 0.40),
    },
    {
      method: PaymentNode.T_T_BALANCE,
      label: 'T/T 尾款',
      percentage: 60,
      amount: Math.round(totalReceivable * 0.60),
    },
    {
      method: PaymentNode.BLU_CREDIT,
      label: 'BluCredit',
      percentage: 23,
      amount: Math.round(totalReceivable * 0.23),
    },
    {
      method: PaymentNode.L_C,
      label: 'L/C',
      percentage: 11,
      amount: Math.round(totalReceivable * 0.11),
    },
  ];
}

// ===== 7. 财务统计计算 =====

export function generateFinanceStats(receivables: FinanceReceivable[]): FinanceStats {
  const totalReceivable = parseFloat(receivables.reduce((s, r) => s + r.amount, 0).toFixed(2));
  const totalPaid = parseFloat(receivables.reduce((s, r) => s + r.paidAmount, 0).toFixed(2));
  const collectionRate = totalReceivable > 0
    ? parseFloat(((totalPaid / totalReceivable) * 100).toFixed(1))
    : 0;
  const pendingAmount = parseFloat((totalReceivable - totalPaid).toFixed(2));

  const pendingDueSoon = parseFloat(
    receivables
      .filter((r) => r.displayStatus === ReceivableDisplayStatus.DUE_SOON)
      .reduce((s, r) => s + (r.amount - r.paidAmount), 0)
      .toFixed(2),
  );

  const overdueReceivables = receivables.filter((r) => r.overdueDays > 0);
  const overdueAmount = parseFloat(
    overdueReceivables.reduce((s, r) => s + (r.amount - r.paidAmount), 0).toFixed(2),
  );
  const overdueCount = overdueReceivables.length;
  const maxOverdueDays = overdueReceivables.length > 0
    ? Math.max(...overdueReceivables.map((r) => r.overdueDays))
    : 0;

  const overdueLabel = overdueCount > 0
    ? `${overdueCount}笔·最长${maxOverdueDays}天`
    : '无逾期';

  return {
    totalReceivable,
    totalPaid,
    collectionRate,
    pendingAmount,
    pendingDueSoon,
    pendingDueSoonLabel: '15日内到期',
    overdueAmount,
    overdueCount,
    maxOverdueDays,
    overdueLabel,
    currency: 'USD',
  };
}

// ===== 生成并导出 =====

/** 应收台账 Mock 数据 (28条) */
/** 从订单派生应收台账 */
function deriveReceivableFromOrder(order: import('../types').Order, index: number): FinanceReceivable {
  const isFullyPaid = index % 5 === 0;
  const paidAmount = isFullyPaid
    ? order.totalAmount
    : parseFloat(randomFloat(0, order.totalAmount * 0.9, 2).toFixed(2));

  const today = new Date();
  const dueOffset = (index - 6) * 5;
  const dueDate = new Date(today.getTime() + dueOffset * 86400000);

  const { displayStatus, overdueDays } = computeDisplayStatus(dueDate, paidAmount, order.totalAmount);
  const waterSlipStatus = deduceWaterSlipStatus(displayStatus);

  const paymentNodes = [
    PaymentNode.T_T_DEPOSIT, PaymentNode.T_T_DEPOSIT, PaymentNode.T_T_DEPOSIT,
    PaymentNode.T_T_BALANCE, PaymentNode.T_T_BALANCE, PaymentNode.T_T_BALANCE, PaymentNode.T_T_BALANCE,
    PaymentNode.FULLY_PAID, PaymentNode.FULLY_PAID,
    PaymentNode.L_C, PaymentNode.BLU_CREDIT,
  ];
  const paymentNode = paymentNodes[index % paymentNodes.length];

  const PAYMENT_NODE_LABELS: Record<PaymentNode, string> = {
    [PaymentNode.T_T_DEPOSIT]: 'T/T 30%定金',
    [PaymentNode.T_T_BALANCE]: 'T/T 70%尾款',
    [PaymentNode.FULLY_PAID]: '全额已付',
    [PaymentNode.L_C]: 'L/C',
    [PaymentNode.BLU_CREDIT]: 'BluCredit',
  };

  const bluCreditApplied = paymentNode === PaymentNode.BLU_CREDIT;

  return {
    id: `FIN${String(index + 1).padStart(3, '0')}`,
    orderId: order.id,
    orderNo: order.orderNo,
    buyerName: order.buyerName,
    amount: order.totalAmount,
    paidAmount,
    currency: order.currency,
    dueDate: dueDate.toISOString(),
    status: PaymentStatus.PARTIALLY_PAID,
    paymentRecords: generatePaymentRecords(index, order.totalAmount, paidAmount),
    bluCreditApplied,
    bluCreditAmount: bluCreditApplied ? parseFloat(randomFloat(5000, 20000, 2).toFixed(2)) : 0,
    waterSlipStatus,
    displayStatus,
    paymentNode,
    paymentNodeLabel: PAYMENT_NODE_LABELS[paymentNode],
    overdueDays,
  };
}

/** 从订单派生应收台账列表 */
export const mockReceivables: FinanceReceivable[] = mockOrders.map((order, i) => deriveReceivableFromOrder(order, i));

/** 收款计划 Mock 数据 (15条) */
export const mockPaymentPlans: PaymentPlan[] = Array.from(
  { length: 15 },
  (_, i) => generatePaymentPlan(i, mockReceivables),
);

/** 水单核销 Mock 数据 (3条) */
export const mockWaterSlips: WaterSlip[] = Array.from(
  { length: 3 },
  (_, i) => generateWaterSlip(i, mockReceivables),
);

/** 汇兑记录 Mock 数据 (12条) */
export const mockExchangeRecords: ExchangeRecord[] = Array.from(
  { length: 12 },
  (_, i) => generateExchangeRecord(i),
);

/** 当前汇率 Mock 数据 (3条) */
export const mockExchangeRates: ExchangeRate[] = generateExchangeRates();

/** 收款结构 Mock 数据 (4项) */
export const mockPaymentStructure: PaymentStructureItem[] = generatePaymentStructure();

/** 财务统计（基于 receivables 全量计算） */
export const mockFinanceStats: FinanceStats = generateFinanceStats(mockReceivables);

// ===== 向后兼容: 保留旧 mockFinances 导出 =====
export const mockFinances: FinanceReceivable[] = mockReceivables;
